import { generateUrlParams } from '@utils';

import { HapvidaApi } from '../../http/Hapvida';
import type {
  BillDetails,
  BillDetailsRequestDTO,
  BillDetailsResponseDTO,
  DiscountLetterRequestDTO,
  DiscountLetterResponseDTO,
  GetReportMonthlyRequestDTO,
  ReportMonthlyProps,
  BillListProps,
  GetBillListRequestDTO,
  GetBillListResponseDTO,
  BillsSummaryRequestDTO,
  BillsSummaryResponseDTO,
  BillSummariesDTO,
  GetCoPaymentReportRequestDTO,
  GetCoPaymentReportResponseDTO,
  InvoiceRequestDTO,
  InvoiceResponseDTO,
  MonthlyFeePreviewReportResponseDTO,
  GenerateIndividualReportRequest,
  GenerateIndividualReportResponse,
  GetCoparticipationPreviewRequestDTO,
  GetCoparticipationPreviewResponseDTO,
  ExportCopartPreviewCSVFileRequestDTO,
  ExportCopartPreviewCSVFileResponseDTO,
  GetExportCopartPreviewCSVFileResponseDTO,
} from './dtos';
import type { CoparticipationPreviewReportResponseDTO } from './dtos/GetCoparticipationPreviewReport';
import type { CoparticipationReportRequestDTO } from './dtos/GetCoparticipationReportResponse';
import {
  GetIncomeReportPrintRequestDTO,
  GetIncomeReportPrintResponseDTO,
} from './dtos/GetIncomeReportPrint';

export * from './dtos';

export default class FinancialService {
  private readonly endpoints = {
    summary: (companyId: string) =>
      `/api/v3/billings/${companyId}/bills/summary`,
    list: (companyId: string) => `/api/v3/billings/${companyId}/bills/search`,
    bill: (billNumber: string) => `/api/v3/billings/bill/${billNumber}`,
    invoice: () => `/api/v3/billings/invoice`,
    reportMonthly: ({ billingNumber, fileType }: GetReportMonthlyRequestDTO) =>
      `/api/v3/billings/report-monthly/billing-number/${billingNumber}/report-type/${fileType}`,
    discountLetter: ({ companyId, invoiceNumber }: DiscountLetterRequestDTO) =>
      `/api/v3/billings/companies/${companyId}/invoice-number/${invoiceNumber}/discount-letters`,
    monthlyFeePreviewReport: () => `/api/v3/billings/preview-report/exists`,
    coparticipationPreviewReport: () =>
      `/api/v1/billings/coparticipation-preview-report/exists`,
    coparticipationReportRequest: ({
      personNumber,
      billingNumber,
    }: CoparticipationReportRequestDTO) =>
      `/api/v1/billings/coparticipation/bill-report?person-number=${personNumber}&billing-number=${billingNumber}`,
    coparticipationGenerateReport: () =>
      `/api/v1/billings/coparticipation/generate-individual-report`,
    coparticipationReportDownload: ({
      personNumber,
      billingExtractId,
      fileName,
    }: GetCoPaymentReportRequestDTO) =>
      `/api/v1/billings/coparticipation?person-number=${personNumber}&billing-extract-id=${billingExtractId}&file-name=${fileName}`,
    incomeReportPrint: ({
      partnerCompany,
      year,
    }: GetIncomeReportPrintRequestDTO) =>
      `/api/v3/billings/${partnerCompany}/income-report/${year}`,
    coparticipationPreview: (partnerCompany: string) =>
      `/api/v3/billings/coparticipation-preview-report/${partnerCompany}`,
    exportCopartPreviewCSVFile: ({
      partnerCompany,
    }: ExportCopartPreviewCSVFileRequestDTO) =>
      `/api/v3/billings/coparticipation-preview-report/${partnerCompany}/generate-report`,
    getExportCopartPreviewCSVFile: () =>
      `/api/v3/billings/coparticipation/status-report`,
  };

  public getDiscountLetters = async (props: DiscountLetterRequestDTO) => {
    const { data } = await HapvidaApi.get<DiscountLetterResponseDTO>(
      this.endpoints.discountLetter(props),
    );

    return data.content;
  };

  public getBillsSummary = async ({
    companyId,
    ...body
  }: BillsSummaryRequestDTO): Promise<BillSummariesDTO> => {
    const params = generateUrlParams(body);

    const { data } = await HapvidaApi.get<BillsSummaryResponseDTO>(
      this.endpoints.summary(companyId),
      {
        params,
      },
    );

    return data.content;
  };

  public getBillsList = async ({
    companyId,
    ...body
  }: GetBillListRequestDTO): Promise<BillListProps> => {
    const params = generateUrlParams(body);

    const { data } = await HapvidaApi.get<GetBillListResponseDTO>(
      this.endpoints.list(companyId),
      {
        params,
      },
    );

    return data.content;
  };

  public getBill = async ({
    billNumber,
    companyId,
  }: BillDetailsRequestDTO): Promise<BillDetails> => {
    const { data } = await HapvidaApi.get<BillDetailsResponseDTO>(
      this.endpoints.bill(billNumber),
      {
        params: {
          'company-id': companyId,
        },
      },
    );

    return data.content;
  };

  public getInvoice = async ({ billingNumber }: InvoiceRequestDTO) => {
    const { data } = await HapvidaApi.get<InvoiceResponseDTO>(
      this.endpoints.invoice(),
      {
        params: {
          'billing-number': billingNumber,
        },
      },
    );

    return data.content;
  };

  public reportMonthly = async ({
    billingNumber,
    fileType,
  }: GetReportMonthlyRequestDTO): Promise<ReportMonthlyProps> => {
    const { data, status } = await HapvidaApi.get<ReportMonthlyProps>(
      this.endpoints.reportMonthly({ billingNumber, fileType }),
    );

    return { ...data, statusCode: status };
  };

  public getCoparticipationReport = async ({
    personNumber,
    billingNumber,
  }: CoparticipationReportRequestDTO) => {
    const { data } = await HapvidaApi.get<GetCoPaymentReportResponseDTO>(
      this.endpoints.coparticipationReportRequest({
        personNumber,
        billingNumber,
      }),
    );

    return data.content;
  };

  public getCoparticipationGenerateReport = async ({
    personNumber,
    remittenceCode,
    fileType,
  }: GenerateIndividualReportRequest) => {
    const { data } = await HapvidaApi.post<GenerateIndividualReportResponse>(
      this.endpoints.coparticipationGenerateReport(),
      {
        'person-number': personNumber,
        'remittence-code': remittenceCode,
        'file-type': fileType,
      },
    );
    return data;
  };

  public getCoparticipationReportDownload = async ({
    personNumber,
    billingExtractId,
    fileName,
  }: GetCoPaymentReportRequestDTO) => {
    const { data } = await HapvidaApi.get<GetCoPaymentReportResponseDTO>(
      this.endpoints.coparticipationReportDownload({
        personNumber,
        billingExtractId,
        fileName,
      }),
    );

    return data.content;
  };

  public getMonthlyFeePreviewReport = async () => {
    const { data } = await HapvidaApi.get<MonthlyFeePreviewReportResponseDTO>(
      this.endpoints.monthlyFeePreviewReport(),
    );

    return data.content;
  };

  public getCoparticipationPreviewReport = async () => {
    const { data } =
      await HapvidaApi.get<CoparticipationPreviewReportResponseDTO>(
        this.endpoints.coparticipationPreviewReport(),
      );

    return data.content;
  };

  public getIncomeReportPrint = async ({
    partnerCompany,
    year,
  }: GetIncomeReportPrintRequestDTO) => {
    const { data } = await HapvidaApi.get<GetIncomeReportPrintResponseDTO>(
      this.endpoints.incomeReportPrint({
        partnerCompany,
        year,
      }),
    );

    return data.content;
  };

  public getCoparticipationPreview = async ({
    partnerCompany,
    ...body
  }: GetCoparticipationPreviewRequestDTO) => {
    const params = generateUrlParams(body);

    const { data } = await HapvidaApi.get<GetCoparticipationPreviewResponseDTO>(
      this.endpoints.coparticipationPreview(partnerCompany),
      {
        params,
      },
    );

    return data.content;
  };

  public async exportCopartPreviewCSVFile({
    partnerCompany,
  }: ExportCopartPreviewCSVFileRequestDTO) {
    const { data } =
      await HapvidaApi.post<ExportCopartPreviewCSVFileResponseDTO>(
        this.endpoints.exportCopartPreviewCSVFile({ partnerCompany }),
      );

    return data.content;
  }

  public async getExportCopartPreviewCSVFile() {
    const { data } =
      await HapvidaApi.get<GetExportCopartPreviewCSVFileResponseDTO>(
        this.endpoints.getExportCopartPreviewCSVFile(),
      );

    return data.content;
  }
}
